import type { ModuleSnapshot } from '@ctcells/contract'
import { hasMessage, t } from '@ctcells/i18n'

/**
 * 6축 «위치» — `GET /modules` 목록의 `ModuleSnapshot.pose_telemetry`(v0.9.4 · 정본 신설 2026-10-08).
 *
 * 실물(dev2 2026-10-11 · `robot6-1`):
 * `{ts, metrics: {"rob6.1.tcp_x_mm":0, …, "rob6.1.tcp_rz_rad":0, "rob6.1.lift_mm":0, "rob6.1.traverse_mm":0, "pose_simulated":1}}`
 * · `mock-6axis-1` 은 `null`.
 *
 * 🔴 **관절각이 아니다** — TCP 좌표 · 자세(rad) · 부가축(상하축 `lift` · 좌우축 `traverse`)이다. 그래서 팔 그림(`ArmFigure` ·
 *    도식 자세 `robotMotionModel.ts`)에 얹지 않는다. 관절(`joint_N_rad`)은 «관절을 읽는 드라이버가 있을 때만» 온다 — 오면 같은 줄에 선다.
 * 🔴 **이름 규칙은 계약이 정했다**(`events.asyncapi` `Telemetry.metrics`): `<robot_id>.<이름>` · 단위는 이름 접미(`_mm` · `_rad`) ·
 *    모의 값이면 `pose_simulated: 1`(실측이면 키가 없다). `robot_id` 에 점이 들어 있으므로(`rob6.1`) **마지막 점 뒤가 이름**이다.
 *    로봇 id 를 코드에 박지 않는다.
 * 🔴 **세 갈래를 접지 않는다**
 *    - `null` = 받은 위치 봉투가 없다(위치를 내지 않는 모듈 포함) → `none`
 *    - 키 부재 = 이 경로가 싣지 않았다(단건 · `/control` · 구독 `module_status` 에는 키가 없다) → `absent`
 *    - 값 있음 → `read`. ⚠ 「0」은 «0 에 있다»이지 «모른다»가 아니다 — 그대로 적는다
 * ⚠ 갱신은 구독 `module_telemetry` 가 목록 캐시를 무효화해 다시 받는 것이다(`stream/bridge.ts` — 캐시에 프레임을 덮어쓰지 않는다).
 *   그래서 이 칸은 **목록 스냅샷에서만** 읽는다.
 * ⚠ `ts` 는 «코어가 그 봉투를 받은 시각»이다(모듈이 적은 시각이 아니다). 위치 봉투는 값이 바뀔 때만 오므로 멈춘 로봇은 옛 시각이 정상이다.
 */

/** 모의 값 표지 키 — 계약 `Telemetry.metrics` 「모의 값이면 `pose_simulated: 1`」 */
export const POSE_SIMULATED_KEY = 'pose_simulated'

export interface PoseReading {
  /** 응답 키 원문(`rob6.1.tcp_x_mm`) */
  key: string
  /** `robot_id` — 이름 앞부분. 점이 없는 키(`slide_mm`)는 `undefined` */
  robot: string | undefined
  /** 이름(`tcp_x_mm`) */
  name: string
  /** 이름 접미가 말하는 단위. 모르는 접미면 `undefined`(지어내지 않는다) */
  unit: 'mm' | 'rad' | undefined
  value: number
}

export type RobotPose =
  /** 키가 없다 — 이 응답은 위치를 싣지 않는 경로다 */
  | { kind: 'absent' }
  /** `null` 이거나 위치 이름이 하나도 없다 — 받은 위치가 없다 */
  | { kind: 'none' }
  | { kind: 'read'; ts: string; simulated: boolean; readings: PoseReading[] }

function unitOf(name: string): PoseReading['unit'] {
  if (name.endsWith('_mm')) return 'mm'
  if (name.endsWith('_rad')) return 'rad'
  return undefined
}

/** 스냅샷의 위치 봉투를 읽는다 — 차례는 응답 그대로(모듈이 낸 차례) */
export function robotPose(snapshot: ModuleSnapshot | undefined): RobotPose {
  if (snapshot === undefined || !('pose_telemetry' in snapshot)) return { kind: 'absent' }
  const pose = snapshot.pose_telemetry
  if (pose == null) return { kind: 'none' }
  const readings: PoseReading[] = []
  for (const [key, value] of Object.entries(pose.metrics)) {
    if (key === POSE_SIMULATED_KEY) continue
    const dot = key.lastIndexOf('.')
    const name = dot < 0 ? key : key.slice(dot + 1)
    readings.push({
      key,
      robot: dot < 0 ? undefined : key.slice(0, dot),
      name,
      unit: unitOf(name),
      value,
    })
  }
  if (readings.length === 0) return { kind: 'none' }
  return {
    kind: 'read',
    ts: pose.ts,
    simulated: pose.metrics[POSE_SIMULATED_KEY] === 1,
    readings,
  }
}

/**
 * 칸 이름 — 아는 이름은 사전(`monitoring.robot.position.<이름>`), 관절은 「관절 n」, 모르는 이름은 **원문 그대로**.
 * 로봇이 둘 이상이면 앞에 로봇 id 를 붙인다(한 모듈 한 로봇이면 붙이지 않는다).
 */
export function poseLabel(reading: PoseReading, robots: number): string {
  const key = `monitoring.robot.position.${reading.name}`
  const joint = /^joint_(\d+)_rad$/.exec(reading.name)
  const base = hasMessage(key)
    ? t(key)
    : joint !== null
      ? t('monitoring.robot.position.joint', { n: joint[1] ?? '' })
      : reading.name
  return robots > 1 && reading.robot !== undefined ? `${reading.robot} ${base}` : base
}

/** 값 — 숫자 그대로 + 접미 단위. 단위를 모르면 숫자만 */
export function poseValue(reading: PoseReading): string {
  return reading.unit === undefined
    ? String(reading.value)
    : `${String(reading.value)} ${reading.unit}`
}

/**
 * 위치 봉투에서 **이름 하나**를 읽는다(`x_mm` · `slide_mm` · `tcp_z_mm` …) — 모듈 안 3축 X · Y · Z 처럼 «그 모듈의 그 축»을 한 칸으로 세우는 자리.
 * 🔴 `null`(받은 봉투 없음) · 키 부재 · 그 이름 없음은 전부 `absent` — 「0」으로 접지 않는다. 같은 이름이 둘 이상이면(로봇 둘) 첫 것.
 */
export type PoseValue =
  | { kind: 'absent' }
  | { kind: 'value'; value: number; unit: PoseReading['unit']; simulated: boolean }

export function poseValueOf(snapshot: ModuleSnapshot | undefined, name: string): PoseValue {
  const pose = robotPose(snapshot)
  if (pose.kind !== 'read') return { kind: 'absent' }
  const reading = pose.readings.find((one) => one.name === name)
  return reading === undefined
    ? { kind: 'absent' }
    : { kind: 'value', value: reading.value, unit: reading.unit, simulated: pose.simulated }
}

/** 이 봉투에 든 로봇 수 — 라벨 앞 로봇 id 를 붙일지 가른다 */
export function poseRobotCount(readings: readonly PoseReading[]): number {
  return new Set(readings.map((reading) => reading.robot)).size
}

/**
 * 화면 차례 — 세 칸 한 줄씩 「TCP RX · RY · RZ」 → 「TCP X · Y · Z」 → 「상하축 · 좌우축」(eric 2026-10-12 「TCP RX, TCP RY, TCP RZ 다음 줄에..
 * TCP X, TCP Y, TCP Z 그 아래에 상하축, 좌우축」). 모르는 이름은 그 뒤에 응답 차례 그대로 · 로봇이 여럿이면 로봇별로 같은 차례
 */
const POSE_DISPLAY_RANK: Readonly<Record<string, number>> = {
  tcp_rx_rad: 0,
  tcp_ry_rad: 1,
  tcp_rz_rad: 2,
  tcp_x_mm: 3,
  tcp_y_mm: 4,
  tcp_z_mm: 5,
  lift_mm: 6,
  traverse_mm: 7,
}

export function poseDisplayOrder(readings: readonly PoseReading[]): PoseReading[] {
  const rank = (reading: PoseReading) => POSE_DISPLAY_RANK[reading.name] ?? Number.MAX_SAFE_INTEGER
  return readings
    .map((reading, index) => ({ reading, index }))
    .sort(
      (a, b) =>
        (a.reading.robot ?? '').localeCompare(b.reading.robot ?? '') ||
        rank(a.reading) - rank(b.reading) ||
        a.index - b.index,
    )
    .map(({ reading }) => reading)
}
